// ABOUTME: Integrations sub-section within the Data Sources tab of the workspace admin panel.
// ABOUTME: Displays the Shopify connector status card and actions, or an empty-state prompt to connect.

import { useEffect, useRef, useState } from 'react';
import {
  ShoppingBag,
  Play,
  Edit,
  Trash2,
  PauseCircle,
  PlayCircle,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { shopifyConnectorApi } from '@/services/api/adminClient';
import { ShopifyConnectorModal } from './ShopifyConnectorModal';
import type { ShopifyConnector, ShopifyConnectorStatus } from '@/types/admin';

interface ShopifyConnectorSectionProps {
  workspaceId: string;
  connector: ShopifyConnector | null | undefined;
  onRefresh: () => void;
}

/** Human-readable label for a sync frequency in minutes. */
function formatFrequency(minutes: number): string {
  if (minutes < 60) return `Every ${minutes} minutes`;
  const hours = minutes / 60;
  return hours === 1 ? 'Every hour' : `Every ${hours} hours`;
}

/** Human-readable relative time (e.g. "3 minutes ago"). */
function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

const STATUS_CONFIG: Record<
  ShopifyConnectorStatus,
  { label: string; color: string; bgColor: string; icon: typeof CheckCircle }
> = {
  active: {
    label: 'Active',
    color: 'text-success',
    bgColor: 'bg-success-light',
    icon: CheckCircle,
  },
  paused: {
    label: 'Paused',
    color: 'text-warning',
    bgColor: 'bg-warning-light',
    icon: PauseCircle,
  },
  error: {
    label: 'Error',
    color: 'text-error',
    bgColor: 'bg-error-light',
    icon: XCircle,
  },
  provisioning: {
    label: 'Provisioning',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    icon: Loader2,
  },
  pending_install: {
    label: 'Pending Install',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    icon: Clock,
  },
};

export function ShopifyConnectorSection({
  workspaceId,
  connector,
  onRefresh,
}: ShopifyConnectorSectionProps) {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Poll for status updates when a run is in progress
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const isRunningStatus = connector?.last_run_status === 'running';
    if (isRunningStatus && !pollRef.current) {
      pollRef.current = setInterval(() => {
        onRefresh();
      }, 5000);
    }
    if (!isRunningStatus && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [connector?.last_run_status, onRefresh]);

  const handleRunNow = async () => {
    setIsRunning(true);
    setActionError(null);
    try {
      await shopifyConnectorApi.triggerRun(workspaceId);
      onRefresh();
    } catch {
      // ECS infra not configured — fall back to local in-process run
      try {
        await shopifyConnectorApi.triggerRunLocal(workspaceId);
        onRefresh();
      } catch (localErr) {
        setActionError(localErr instanceof Error ? localErr.message : 'Failed to trigger run');
      }
    } finally {
      setIsRunning(false);
    }
  };

  const handleTogglePause = async () => {
    if (!connector) return;

    setIsToggling(true);
    setActionError(null);
    try {
      const newStatus = connector.status === 'paused' ? 'active' : 'paused';
      await shopifyConnectorApi.update(workspaceId, { status: newStatus });
      onRefresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update connector');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setActionError(null);
    try {
      await shopifyConnectorApi.remove(workspaceId);
      setShowDeleteConfirm(false);
      onRefresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete connector');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConnectorSaved = (saved: ShopifyConnector) => {
    // saved is passed through; we trigger a full refresh so the parent re-fetches
    void saved;
    onRefresh();
  };

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-3 pt-2">
        <h3 className="text-base font-medium text-surface-700">Integrations</h3>
        <hr className="flex-1 border-surface-200" />
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="p-3 rounded-sm bg-error-light border border-error/30 text-error text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {actionError}
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <Card variant="surface" className="border-error/30 bg-error-light">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-sm bg-error/20">
              <AlertTriangle className="w-5 h-5 text-error" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-surface-800">Remove Shopify Connector</h4>
              <p className="text-sm text-surface-600 mt-1">
                This will disconnect the Shopify integration and stop all future syncs. Previously
                synced data will not be deleted.
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  isLoading={isDeleting}
                >
                  Remove Connector
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {!connector ? (
        <Card variant="glass" className="p-8 text-center">
          <ShoppingBag className="w-12 h-12 text-surface-400 mx-auto mb-4" />
          <p className="text-surface-600 font-medium">No integrations configured</p>
          <p className="text-sm text-surface-500 mt-1">
            Connect a Shopify store to automatically sync your commerce data
          </p>
          <Button
            size="sm"
            className="mt-4"
            leftIcon={<ShoppingBag className="w-4 h-4" />}
            onClick={() => setShowModal(true)}
          >
            Connect Shopify
          </Button>
        </Card>
      ) : (
        /* Connector status card */
        <Card variant="surface">
          <div className="flex items-start justify-between flex-wrap gap-4">
            {/* Left: icon + details */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-primary-50 flex items-center justify-center shrink-0">
                <ShoppingBag className="w-5 h-5 text-primary-600" />
              </div>
              <div className="space-y-2">
                {/* Shop URL + status */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-surface-800">{connector.shop_url}</span>
                  <ConnectorStatusBadge status={connector.status} />
                </div>

                {/* Resources */}
                <div className="flex flex-wrap gap-1.5">
                  {connector.resources.map((r) => (
                    <span
                      key={r}
                      className="px-2 py-0.5 text-xs font-medium rounded-sm bg-surface-100 text-surface-600 capitalize"
                    >
                      {r}
                    </span>
                  ))}
                </div>

                {/* Frequency + last run */}
                <div className="flex items-center gap-4 text-xs text-surface-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {formatFrequency(connector.schedule_frequency_minutes)}
                  </span>
                  {(connector.last_run_at || connector.last_run_status) && (
                    <LastRunBadge
                      lastRunAt={connector.last_run_at}
                      lastRunStatus={connector.last_run_status}
                    />
                  )}
                </div>

                {/* Error message if last run failed */}
                {connector.last_run_error && (connector.status === 'error' || connector.last_run_status === 'failed') && (
                  <p className="text-xs text-error">{connector.last_run_error}</p>
                )}
              </div>
            </div>

            {/* Right: action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRunNow}
                isLoading={isRunning}
                title="Trigger a sync run now"
              >
                <Play className="w-4 h-4 mr-1" />
                Run Now
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowModal(true)}
                title="Edit connector settings"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTogglePause}
                isLoading={isToggling}
                title={connector.status === 'paused' ? 'Resume syncing' : 'Pause syncing'}
              >
                {connector.status === 'paused' ? (
                  <>
                    <PlayCircle className="w-4 h-4 mr-1" />
                    Resume
                  </>
                ) : (
                  <>
                    <PauseCircle className="w-4 h-4 mr-1" />
                    Pause
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-error hover:text-error"
                title="Remove this connector"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create / Edit modal */}
      <ShopifyConnectorModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        workspaceId={workspaceId}
        connector={connector ?? undefined}
        onSaved={handleConnectorSaved}
      />
    </div>
  );
}

function ConnectorStatusBadge({ status }: { status: ShopifyConnectorStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.error;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm ${cfg.bgColor} ${cfg.color}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function LastRunBadge({
  lastRunAt,
  lastRunStatus,
}: {
  lastRunAt?: string;
  lastRunStatus?: string;
}) {
  const colorByStatus: Record<string, string> = {
    success: 'text-success',
    failed: 'text-error',
    running: 'text-primary-600',
  };
  const color = colorByStatus[lastRunStatus ?? ''] ?? 'text-surface-500';

  let label = 'No runs yet';
  if (lastRunStatus === 'running') {
    label = 'Running...';
  } else if (lastRunAt) {
    label = `Last run ${formatRelativeTime(lastRunAt)}`;
  }

  return (
    <span className={`flex items-center gap-1 ${color}`}>
      {lastRunStatus === 'success' && <CheckCircle className="w-3.5 h-3.5" />}
      {lastRunStatus === 'failed' && <XCircle className="w-3.5 h-3.5" />}
      {lastRunStatus === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {label}
    </span>
  );
}

export default ShopifyConnectorSection;
